import React, { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import CommonRegisterComponent from "./CommonRegisterComponent";
import RegisterFormComponent from "./RegisterFormComponent";
import { Link } from "react-router-dom";
import Card, { CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { useTranslation } from "react-i18next";

const RegisterComponent = () => {
    const { t } = useTranslation();
    const [userType, setUserType] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const handleUserTypeSelect = (type) => {
        setUserType(type);
    };

    const handleContinueWithEmail = () => {
        if (userType) {
            setShowForm(true);
        } else {
            alert("Please select a user type first");
        }
    };

    const handleLogin = () => {
        window.location.href = "/auth/login";
    };
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-100 flex flex-col p-4">
            {/* Navigation Header */}
            <div className="max-w-7xl mx-auto w-full py-6 px-4">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center"
                >
                    <Link to={"/"}
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        <span>{t("registerPage.backToHome")}</span>
                    </Link>
                </motion.div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 items-center p-4">
                    {/* Left Side - Information */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="hidden md:block space-y-6"
                    >
                        <div className="text-2xl font-extrabold text-blue-600 flex items-center gap-3 text-left">
                            <img
                                src="https://assests.netlify.app/assets/images/logo.png"
                                alt="Kokobeo Logo"
                                className="w-12 h-12 object-contain"
                            />
                            <span>Kokobeo</span>
                        </div>
                        <div className="space-y-4 text-left">
                            <h1 className="text-4xl font-bold text-gray-900">
                                {t("registerPage.title")}
                            </h1>
                            <p className="text-lg text-gray-600">
                                {t("registerPage.subtitle")}
                            </p>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <svg
                                        className="w-6 h-6 text-blue-600"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                </div>
                                <p className="text-gray-700">
                                    {t("registerPage.benefits.getMatched")}
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <svg
                                        className="w-6 h-6 text-blue-600"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                                <p className="text-gray-700">{t("registerPage.benefits.setRates")}</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <svg
                                        className="w-6 h-6 text-blue-600"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                        />
                                    </svg>
                                </div>
                                <p className="text-gray-700">{t("registerPage.benefits.securePayments")}</p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Right Side - Registration Form */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <Card color={undefined} className="border-0 shadow-2xl bg-white/80 backdrop-blur">
                            <CardHeader color={undefined} className="pb-6">
                                <div className="md:hidden mb-6 flex justify-center">
                                    <div className="text-2xl font-extrabold text-blue-600 flex items-center gap-3">
                                        <img
                                            src="https://assests.netlify.app/assets/images/logo.png"
                                            alt="Kokobeo Logo"
                                            className="w-10 h-10 object-contain"
                                        />
                                        <span>Kokobeo</span>
                                    </div>
                                </div>
                                {!showForm && <CardTitle color={undefined} className="text-2xl font-bold text-center text-gray-900">
                                    {t("registerPage.createAccount")}
                                </CardTitle>}
                            </CardHeader>

                            <CardContent className="space-y-4">
                                {/* User Type Selection */}
                                {!showForm ? (
                                    <CommonRegisterComponent
                                        handleContinueWithEmail={handleContinueWithEmail}
                                        handleLogin={handleLogin}
                                        onSelectUserType={handleUserTypeSelect}
                                        selectedUserType={userType}
                                    />
                                ) : (
                                    <RegisterFormComponent userType={userType} />
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default RegisterComponent;